import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { llamarWhatsappConfig, type EstadoConexionWhatsapp } from "@/hooks/useWhatsappEvolution";

type RespuestaQr = {
  qr: string | null;
  pairing_code?: string | null;
  conectado: boolean;
  estado?: EstadoConexionWhatsapp | string | null;
  detalle?: string;
};

/**
 * QR para volver a vincular la instancia sin entrar al manager de Evolution. El QR caduca
 * en ~40 s: se pide de nuevo cada 25 s y, mientras está activo, se revisa el estado cada
 * 5 s para avisar en cuanto el teléfono lo escanee. Lo usan la pantalla de configuración
 * y el monitor del header (roles con `conectar_telefono_notificaciones`).
 */
export function QrVincularWhatsapp({
  activo,
  onConectado,
}: {
  activo: boolean;
  onConectado: (e: EstadoConexionWhatsapp) => void;
}) {
  const qr = useQuery({
    queryKey: ["whatsapp-evolution-qr"],
    queryFn: () => llamarWhatsappConfig<RespuestaQr>({ action: "qr" }),
    enabled: activo,
    refetchInterval: 25_000,
    gcTime: 0,
    retry: false,
  });

  const estado = useQuery({
    queryKey: ["whatsapp-evolution-estado-qr"],
    queryFn: async () =>
      (
        await llamarWhatsappConfig<{ estado: EstadoConexionWhatsapp }>({
          action: "estado",
          forzar: true,
        })
      ).estado,
    enabled: activo,
    refetchInterval: 5_000,
    gcTime: 0,
  });

  const conectadoQr =
    qr.data?.conectado && qr.data.estado && typeof qr.data.estado === "object" ? qr.data.estado : null;
  const conectado = conectadoQr ?? (estado.data?.estado === "conectado" ? estado.data : null);
  useEffect(() => {
    if (activo && conectado) onConectado(conectado);
  }, [activo, conectado, onConectado]);

  if (!activo) return null;

  return (
    <div className="flex flex-col items-center gap-3">
      {qr.isLoading ? (
        <div className="flex h-64 w-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : qr.error ? (
        <p className="text-sm text-destructive">{(qr.error as Error).message}</p>
      ) : qr.data?.qr ? (
        <>
          <img
            src={qr.data.qr}
            alt="Código QR para vincular WhatsApp"
            className="h-64 w-64 rounded-md border bg-white p-2"
          />
          {qr.data.pairing_code && (
            <p className="text-sm text-muted-foreground">
              O vincula con número de teléfono usando el código{" "}
              <span className="font-mono font-semibold text-foreground">{qr.data.pairing_code}</span>
            </p>
          )}
          <p className="text-xs text-muted-foreground">El código se renueva solo cada 25 s. Se cierra al conectar.</p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {qr.data?.detalle ?? "Evolution no devolvió un código QR. Intenta de nuevo en unos segundos."}
        </p>
      )}
    </div>
  );
}
